import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Users, Globe, Palette, Calendar as CalIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';

const RANGES = [
  ['7d', '7 days'],
  ['30d', '30 days'],
  ['90d', '90 days'],
  ['lifetime', 'Lifetime'],
];

const GENDER_COLORS = { male: '#1E4FD8', female: '#B91C1C', other: '#B38138' };
const RACE_PALETTE = ['#0F0F10', '#B91C1C', '#1E4FD8', '#B38138', '#15803D', '#8B5CF6', '#EA580C', '#0891B2', '#DB2777', '#4B4B52'];

function fmtInt(n) { return Number(n || 0).toLocaleString(); }

function SectionTitle({ icon: Icon, kicker, title, right }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] flex items-center gap-1.5">
          <Icon size={11} strokeWidth={2.4} /> {kicker}
        </p>
        <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">{title}</h2>
      </div>
      {right}
    </div>
  );
}

/** Simple horizontal bar list — used for race + country ratios. */
function RatioBars({ rows, keyField, palette, testId, emptyLabel }) {
  const total = rows.reduce((s, r) => s + (r.count || 0), 0) || 1;
  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-black/[0.08] p-8 text-center text-[13px] text-[#8A8A93]" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white overflow-hidden" data-testid={testId}>
      {rows.map((r, i) => {
        const pct = (r.count / total) * 100;
        const color = palette[i % palette.length];
        return (
          <div key={r[keyField]} className="p-4 border-b border-black/[0.06] last:border-0">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                <span className="text-[14px] font-semibold text-[#0F0F10] truncate">{r[keyField]}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-[12px] tabular-nums text-[#4B4B52]">{fmtInt(r.count)}</span>
                <span className="text-[12px] font-bold tabular-nums text-[#0F0F10]" style={{ minWidth: 48, textAlign: 'right' }}>
                  {pct.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminRatios() {
  const navigate = useNavigate();
  const [range, setRange] = useState('lifetime');
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/demographics?date_range=${range}`)
      .then((r) => setDemo(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Admin access required'); navigate('/admin', { replace: true }); })
      .finally(() => setLoading(false));
  }, [range, navigate]);

  if (loading || !demo) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }

  const g = demo.gender?.totals || {};
  const total = g.all || 1;
  const genderRows = [
    { name: 'Male',   value: g.male   || 0, key: 'male' },
    { name: 'Female', value: g.female || 0, key: 'female' },
    { name: 'Other',  value: g.other  || 0, key: 'other' },
  ];

  const raceRows    = demo.by_race || [];
  const countryRows = demo.by_country || [];
  const ageRows     = demo.by_age || [];
  const maxAge = Math.max(1, ...ageRows.map((r) => r.total || 0));

  return (
    <div className="min-h-screen bg-white pb-[80px]" data-testid="admin-ratios-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          data-testid="ratios-back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">ADMIN · DEMOGRAPHICS</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">Ratios</h1>
        </div>
      </div>

      {/* Range pills */}
      <div className="px-5 mb-5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="ratios-range">
        {RANGES.map(([code, label]) => (
          <button
            key={code}
            onClick={() => setRange(code)}
            data-testid={`ratios-range-${code}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${
              range === code
                ? 'bg-[#0F0F10] text-white border-[#0F0F10]'
                : 'bg-white text-[#4B4B52] border-[var(--hairline-strong)] hover:bg-black/[0.02]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Gender */}
      <section className="px-5 mb-6">
        <SectionTitle icon={Users} kicker="GENDER" title="Gender ratio" />
        <div className="rounded-2xl border border-black/[0.06] bg-white p-4" data-testid="gender-ratio">
          <div className="flex items-center gap-5">
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={genderRows}
                  dataKey="value"
                  innerRadius={38}
                  outerRadius={62}
                  paddingAngle={2}
                  stroke="none"
                >
                  {genderRows.map((r) => (
                    <Cell key={r.key} fill={GENDER_COLORS[r.key]} />
                  ))}
                </Pie>
                <ReTooltip formatter={(v, n) => [fmtInt(v), n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {genderRows.map((r) => {
                const pct = total ? (r.value / total) * 100 : 0;
                return (
                  <div key={r.key} className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-[13px] text-[#0F0F10]">
                      <span className="w-2 h-2 rounded-full" style={{ background: GENDER_COLORS[r.key] }} /> {r.name}
                    </span>
                    <span className="text-[12px] font-bold tabular-nums">
                      {fmtInt(r.value)} <span className="text-[#8A8A93]">· {pct.toFixed(1)}%</span>
                    </span>
                  </div>
                );
              })}
              <p className="text-[10.5px] text-[#8A8A93] mt-3 pt-2 border-t border-black/[0.05]">
                Total profiles: <b className="text-[#0F0F10]">{fmtInt(total)}</b>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Age */}
      <section className="px-5 mb-6">
        <SectionTitle icon={CalIcon} kicker="AGE" title="Age distribution" />
        <div className="rounded-2xl border border-black/[0.06] bg-white overflow-hidden" data-testid="age-ratio">
          {ageRows.map((r) => {
            const pct = (r.total / maxAge) * 100;
            const mPct = r.total ? (r.male / r.total) * 100 : 0;
            return (
              <div key={r.bucket} className="p-4 border-b border-black/[0.06] last:border-0">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[14px] font-semibold text-[#0F0F10]">{r.bucket}</span>
                  <div className="flex items-center gap-2 text-[12px] tabular-nums">
                    <span className="text-[#1E4FD8]">M {fmtInt(r.male)}</span>
                    <span className="text-[#B91C1C]">F {fmtInt(r.female)}</span>
                    <span className="text-[#0F0F10] font-bold ml-1">{fmtInt(r.total)}</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden flex">
                  <div className="h-full transition-all duration-700" style={{ width: `${pct * (mPct / 100)}%`, background: '#1E4FD8' }} />
                  <div className="h-full transition-all duration-700" style={{ width: `${pct * ((100 - mPct) / 100)}%`, background: '#B91C1C' }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Race */}
      <section className="px-5 mb-6">
        <SectionTitle icon={Palette} kicker="RACE / ETHNICITY" title="Ratio by race" />
        <RatioBars rows={raceRows} keyField="race" palette={RACE_PALETTE}
          testId="race-ratio"
          emptyLabel="No race/ethnicity data yet — users haven’t self-reported."
        />
      </section>

      {/* Country */}
      <section className="px-5 mb-8">
        <SectionTitle icon={Globe} kicker="COUNTRY" title="Ratio by country" />
        <BackfillCountryCard onDone={() => api.get(`/admin/demographics?date_range=${range}`).then((r) => setDemo(r.data)).catch(() => {})} />
        <RatioBars rows={countryRows} keyField="country" palette={RACE_PALETTE}
          testId="country-ratio"
          emptyLabel="No country data yet — profiles store city only. Once country is captured on signup, it will populate here."
        />
      </section>
    </div>
  );
}

function BackfillCountryCard({ onDone }) {
  const [progress, setProgress] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        const r = await api.get('/admin/backfill/country/progress');
        if (!alive) return;
        setProgress(r.data);
        if (r.data?.status === 'running') {
          timer = setTimeout(tick, 4000);
        } else if (r.data?.status === 'done') {
          onDone?.();
        }
      } catch { /* ignore */ }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [onDone]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post('/admin/backfill/country');
      toast.success('Backfill started');
      const r = await api.get('/admin/backfill/country/progress');
      setProgress(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start');
    } finally {
      setStarting(false);
    }
  };

  const status = progress?.status;
  const running = status === 'running';
  const done = status === 'done';
  const pct = progress?.total ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div className="mb-3 rounded-2xl p-4"
      style={{ background: running ? '#EEF4FF' : done ? '#F0FDF4' : '#FEF7ED', border: '1px solid #0000000d' }}
      data-testid="country-backfill-card">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13.5px] font-bold text-[#0F0F10]">
            {running ? 'Backfilling countries…' : done ? 'Backfill complete' : 'Backfill existing profiles'}
          </p>
          <p className="text-[11.5px] text-[#4B4B52] mt-0.5 leading-snug">
            {running ? `${progress.processed}/${progress.total} · ${pct}% · ${progress.updated} updated · ${progress.unique_cities_cached} cities cached`
             : done ? `${progress.updated} of ${progress.total} profiles updated. Chart should now reflect real ratios.`
             : 'Resolve city → country via Nominatim so this chart populates instantly. Runs in the background (~1 req/sec + city cache).'}
          </p>
        </div>
        {!running && (
          <button
            onClick={start}
            disabled={starting}
            data-testid="country-backfill-start"
            className="flex-shrink-0 rounded-full text-white font-bold hover:brightness-110 transition-all disabled:opacity-50"
            style={{ background: '#0F0F10', padding: '8px 14px', fontSize: 12 }}
          >
            {starting ? '…' : done ? 'Run again' : 'Start'}
          </button>
        )}
      </div>
      {running && (
        <div className="mt-2 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700"
               style={{ width: `${pct}%`, background: '#1E4FD8' }} />
        </div>
      )}
    </div>
  );
}
